// <s-product-recommendations> loads Shopify's related-product recommendations for the current product
// from the `.json` form of routes.product_recommendations_url and renders each one by cloning the
// Liquid-authored <template> card, so copy and styling stay in the theme. If nothing comes back, the server-rendered picks stay.
class ProductRecommendations extends HTMLElement {
  async connectedCallback() {
    if (this.dataset.loaded) return;
    this.dataset.loaded = 'true';

    const limit = Number(this.dataset.limit || 4);
    const url = new URL(this.dataset.url, window.location.origin);
    url.searchParams.set('product_id', this.dataset.productId);
    url.searchParams.set('limit', String(limit));
    url.searchParams.set('intent', 'related');

    try {
      const response = await fetch(url.toString());
      if (!response.ok) return;
      const data = await response.json();
      const products = (data.products || []).slice(0, limit);
      if (products.length === 0) return;

      const template = this.querySelector('template');
      const list = this.querySelector('[data-list]');
      if (!template || !list) return;
      // Swap the server-rendered starting picks for Shopify recommendations.
      list.replaceChildren();

      for (const product of products) {
        const card = template.content.firstElementChild.cloneNode(true);
        card.querySelectorAll('a').forEach((link) => {
          link.href = product.url;
        });

        const image = card.querySelector('img');
        const imageSrc = product.featured_image;
        if (image && imageSrc) {
          image.src = sizedImage(imageSrc, 600);
          image.srcset = [300, 600, 900].map((width) => `${sizedImage(imageSrc, width)} ${width}w`).join(', ');
          image.alt = product.title;
        } else if (image) {
          image.remove();
        }

        const title = card.querySelector('[data-title]');
        if (title) title.textContent = product.title;

        const price = card.querySelector('[data-price]');
        if (price) price.textContent = formatMoney(product.price, this.dataset.moneyFormat);

        list.append(card);
      }

      this.hidden = false;
    } catch (error) {
      // On any failure the server-rendered picks stay in place.
    }
  }
}

function sizedImage(src, width) {
  const url = new URL(src, window.location.href);
  url.searchParams.set('width', String(width));
  return url.toString();
}

// Mirrors Shopify's money format placeholders; the endpoint returns prices in cents.
function formatMoney(cents, format) {
  const moneyFormat = format || '${{amount}}';
  const value = Number(cents) / 100;
  const withDelimiters = (amount, decimals, thousands, decimal) => {
    const fixed = amount.toFixed(decimals);
    const [whole, fraction] = fixed.split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return fraction ? `${grouped}${decimal}${fraction}` : grouped;
  };
  return moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, (match, placeholder) => {
    switch (placeholder) {
      case 'amount_no_decimals':
        return withDelimiters(value, 0, ',', '.');
      case 'amount_with_comma_separator':
        return withDelimiters(value, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator':
        return withDelimiters(value, 0, '.', ',');
      case 'amount_with_apostrophe_separator':
        return withDelimiters(value, 2, "'", '.');
      default:
        return withDelimiters(value, 2, ',', '.');
    }
  });
}

if (!customElements.get('s-product-recommendations')) {
  customElements.define('s-product-recommendations', ProductRecommendations);
}
